import React, { useMemo, memo } from 'react'
import { formatNumberEng } from '../helper/formatNumber'
import useDashboardRedirect from '../hooks/useDashboardRedirect'
import { COLOR_PANEL } from '../../../utils/constant'

const SKELETON_ITEMS = [1, 2, 3, 4, 5]
const MAX_DISPLAY_ITEMS = 5
const SKELETON_EVENTS = [1, 2, 3, 4]
const MAX_DISPLAY_EVENTS = 5

function ProductsAndMembers({
  eventReport,
  buyerReport,
  loading,
  isViewAll,
  filters,
  eventTimeRange
}) {
  const { redirectToManageOrders } = useDashboardRedirect()

  const activeEvents = useMemo(() => {
    const rawEvents = eventReport?.eventStatus || []
    return [...rawEvents]
      .sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0))
      .slice(0, MAX_DISPLAY_EVENTS)
  }, [eventReport?.eventStatus])

  const maxSales = useMemo(
    () => Math.max(...activeEvents.map((e) => e.totalSales || 0), 1),
    [activeEvents]
  )

  const activeBuyers = useMemo(() =>
    (buyerReport || []).slice(0, MAX_DISPLAY_ITEMS), [buyerReport])

  return (
    <div className='products-members-section'>
      <div className='section-label'>
        Products &amp; Members
      </div>
      <div className='bottom-grid'>
        {/* Top events */}
        <div className='panel-card'>
          <div className='panel-header'>
            <span className='panel-title'>Top Events</span>
          </div>
          <div className='panel-body'>
            {loading
              ? SKELETON_EVENTS.map((i) => (
                  <div key={i} className='list-top-event loading'>
                    <div className='product-info'>
                      <span
                        className='skeleton-loader'
                        style={{ width: '100px', height: '14px' }}
                      />
                      <span className='skeleton-loader' style={{ width: '60px', height: '12px' }} />
                    </div>
                    <div className='skeleton-loader product-progress-bg' style={{ width: '100%' }} />
                  </div>
                ))
              : activeEvents.map((evt, idx) => {
                  const name = evt.eName || ''
                  const totalSales = evt.totalSales || 0
                  const pct = Math.round((totalSales / maxSales) * 100)

                  return (
                    <div
                      key={evt.eId || idx}
                      className={`list-top-event ${isViewAll ? 'disabled-buyer' : ''}`}
                      style={{ cursor: isViewAll ? 'not-allowed' : 'pointer' }}
                      onClick={() => {
                        if (isViewAll || !evt.eId) return
                        redirectToManageOrders({
                          eventId: evt.eId,
                          dateRangeOrder: { start: filters?.start, end: filters?.end },
                          dateRangeEvent: eventTimeRange,
                          status: filters?.status,
                        })
                      }}
                    >
                      <div className='product-info'>
                        <span className='product-name'>{name}</span>
                        <span className='product-meta'>
                          ${formatNumberEng(totalSales, 2)}
                        </span>
                      </div>
                      <div className='product-progress-bg'>
                        <div
                          className='product-progress-fill'
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                    </div>
                  )
                })}
          </div>
        </div>

        {/* Top buyers */}
        <div className='panel-card'>
          <div className='panel-header'>
            <span className='panel-title'>Top Buyers</span>
          </div>

          <div className='buyers-list'>
            {loading
              ? SKELETON_ITEMS.map((i) => (
                  <div key={i} className='buyer-item'>
                    <span className='skeleton-loader' style={{ width: '16px', height: '12px' }} />
                    <span
                      className='skeleton-loader buyer-avatar'
                      style={{ borderRadius: '50%' }}
                    />
                    <div className='buyer-info loading'>
                      <span className='skeleton-loader' style={{ width: '80px', height: '12px' }} />
                      <span className='skeleton-loader' style={{ width: '60px', height: '10px' }} />
                    </div>
                    <span
                      className='skeleton-loader'
                      style={{ width: '55px', height: '16px', borderRadius: '8px' }}
                    />
                  </div>
                ))
              : activeBuyers.map((buyer, idx) => {
                  const rank = buyer.rank !== undefined ? buyer.rank : idx + 1
                  const name = buyer.bName || ''
                  const orders = buyer.totalRef || 0
                  const spend = buyer.totalPayment || 0
                  const photoUrl = buyer.photoUrl || null
                  const bg = COLOR_PANEL.TEAL

                  let { badge } = buyer
                  let { badgeClass } = buyer
                  if (!badge) {
                    if (idx === 0) {
                      badge = 'Top buyer'
                      badgeClass = 'teal'
                    } else {
                      badge = 'Active'
                      badgeClass = 'gray'
                    }
                  }

                  const initial = name ? name.charAt(0).toUpperCase() : ''

                  return (
                    <div
                      key={idx}
                      className={`buyer-item ${isViewAll ? 'disabled-buyer' : ''}`}
                      onClick={() => {
                        if (isViewAll) return
                        redirectToManageOrders({
                          dateRangeOrder: { start: filters?.start, end: filters?.end },
                          dateRangeEvent: eventTimeRange,
                          searchString: buyer.phone,
                          eventId: filters?.eid,
                          status: filters?.status,
                        })
                      }}
                    >
                      <div className='buyer-rank'>{rank}</div>
                      {photoUrl ? (
                        <img src={photoUrl} alt={name} className='buyer-avatar' />
                      ) : (
                        <div
                          className='buyer-avatar avatar-placeholder'
                          style={{ background: bg }}
                        >
                          {initial}
                        </div>
                      )}
                      <div className='buyer-info'>
                        <div className='buyer-name'>{name}</div>
                        <div className='buyer-meta'>
                          {orders} orders &middot; $ {formatNumberEng(spend, 2)}
                        </div>
                      </div>
                      <span className={`badge ${badgeClass || 'gray'}`}>{badge}</span>
                    </div>
                  )
                })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(ProductsAndMembers)
